import { createFileRoute } from "@tanstack/react-router";
import perfNow from "common/utils/perfNow";
import { EnvPreset } from "gfx/EnvPreset";
import { formatBytes } from "gfx/MeshDataStats";
import PerspectiveControls from "gfx/PerspectiveControls";
import { envPresetSceneLights } from "gfx/sceneLights";
import THREEViewer from "gfx/THREEViewer";
import { WebGLMultiContextResourceTracker } from "gfx/WebGLContextResourceTracker";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { geometryStressTests, GeometryStressTestType } from "test/util/geometryStressTestUtil";
import type * as THREE from "three";
import Checkbox from "ui/forms/CheckboxOld";
import Select from "ui/forms/Select";
import withAuthentication from "../../../components/withAuthentication";
import styles from "../styles/gfxDebugPage.module.scss";

const defaultCameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 0,
  y: 0,
  z: 2000,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 1,
  upZ: 0,
  cameraNear: 5,
  cameraFar: 10000,
};

function GeometryStressTestPage() {
  const [testType, setTestType] = useState<GeometryStressTestType>(GeometryStressTestType.GEOMETRY_REALLOCATION);

  const [geometryCount, setGeometryCount] = useState<number>(0);
  const [gpuMemoryCounter, setGpuMemoryCounter] = useState<number>(0);
  const [totalBufferAllocations, setTotalBufferAllocations] = useState<number>(0);
  const [lastPerformanceResult, setLastPerformanceResult] = useState<number>(0);
  const [allocationCountLogScale, setAllocationCountLogScale] = useState<number>(0);
  const [allocationSizeLogScale, setAllocationSizeLogScale] = useState<number>(0);

  const [testRunning, setTestRunning] = useState<boolean>(false);
  const [testStep, setTestStep] = useState<number>(0);
  const [autoStop, setAutoStop] = useState<boolean>(false);

  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<THREEViewer | undefined>(undefined);

  const currentMeshesRef = useRef<THREE.Mesh[] | undefined>(undefined);

  // We run each test step in an useEffect callback so that we can read up-to date React state
  useEffect(() => {
    if (!testRunning) {
      return;
    }

    setTimeout(() => {
      if (!testRunning) {
        return;
      }

      const viewer = viewerRef.current;
      if (!viewer) {
        return;
      }

      const t0 = perfNow();

      const test = geometryStressTests[testType];

      const generatedCount = Math.max(1, Math.round(test.increment * Math.pow(10.0, allocationCountLogScale)));
      if (currentMeshesRef.current === undefined) {
        currentMeshesRef.current = [];
      }
      const currentMeshes = currentMeshesRef.current;

      const totalNewBufferAllocations = test.addGeometriesToScene(currentMeshes, generatedCount, {
        viewer,
        allocationSizeMultiplier: Math.pow(10.0, allocationSizeLogScale),
      });
      setTotalBufferAllocations(totalBufferAllocations + totalNewBufferAllocations);

      // We could force a render and finish here if we wanted to measure end-to-end GPU load time, but our priority in this test is to run as fast as possible.
      // We also want GPU pipelining.
      const t1 = perfNow();
      const measurement = t1 - t0;
      console.log("Last performance test result: " + measurement + " milliseconds");
      setLastPerformanceResult(measurement);
      setGeometryCount(currentMeshes.length);

      setGpuMemoryCounter(WebGLMultiContextResourceTracker.instance?.getTotalMemoryUsage() ?? 0);

      const nextTestStep = testStep + 1;
      if (nextTestStep >= 200 && autoStop) {
        setTestRunning(false);
        return;
      }

      setTestStep(nextTestStep);
    }, 50);
  }, [testRunning, testStep]);

  useEffect(() => {
    // Note that not loading the env map will result in the placeholder env map being used.
    const sceneLights = {
      ...envPresetSceneLights[EnvPreset.WHITEBOX],
      interiorAmbientLight: undefined, // disable the interior ambient light
    };

    const viewer = new THREEViewer({
      container: viewerContainerRef.current,
      options: {
        gui: false,
        ssfxRenderer: true,
        animate: true,
        sceneLights,
        debugFeaturesEnabled: true,
        cameraSettings: defaultCameraSettings,
      },
    });

    void (async () => {
      viewerRef.current = viewer;
    })();

    return () => {
      if (viewerRef.current) {
        const viewer = viewerRef.current;
        viewer.scene.clear();
        viewer.dispose();
        viewerRef.current = undefined;
      }
    };
  }, []);

  function removeMeshes() {
    const currentMeshes = currentMeshesRef.current;
    const viewer = viewerRef.current;
    if (currentMeshes === undefined || !viewer) {
      return;
    }
    for (let meshIndex = 0; meshIndex < currentMeshes.length; ++meshIndex) {
      const mesh = currentMeshes[meshIndex];
      if (!mesh) {
        continue;
      }
      viewer.scene.remove(mesh);
    }
    currentMeshesRef.current = [];
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Geometry Stress Test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div>
        <div className={styles.fullScreenViewerContainer} ref={viewerContainerRef} />
        <div className={styles.buttonContainerBottomLeft}>
          <p>
            Allocation count multiplier:{" "}
            <input
              type="range"
              value={allocationCountLogScale}
              min={-1}
              max={2}
              step={0.25}
              onChange={(e) => setAllocationCountLogScale(Number(e.target.value))}
            />
            {Math.pow(10.0, allocationCountLogScale).toFixed(2)}
          </p>
          <p>
            Allocation size multiplier:{" "}
            <input
              type="range"
              value={allocationSizeLogScale}
              min={-1}
              max={2}
              step={0.25}
              onChange={(e) => setAllocationSizeLogScale(Number(e.target.value))}
            />
            {Math.pow(10.0, allocationSizeLogScale).toFixed(2)}
          </p>
          <div>
            Number of geometries: {geometryCount}. GPU memory in use: {formatBytes(gpuMemoryCounter)}. Total buffer
            allocations: {formatBytes(totalBufferAllocations)}
          </div>
          <button
            onClick={() => {
              const viewer = viewerRef.current;
              if (!viewer) {
                return;
              }
              viewer.setCameraSettings(defaultCameraSettings);
              const cameraControls = new PerspectiveControls(viewer.camera, viewer.container, 0, 0, 0);
              viewer.setControls(cameraControls);
            }}
          >
            Reset Camera
          </button>
          <button
            onClick={() => {
              if (!viewerRef.current) {
                return;
              }
              if (!testRunning) {
                setTestStep(0);
              }
              setTestRunning(!testRunning);
            }}
          >
            Start/Stop Testing
          </button>
          <button
            onClick={() => {
              const renderer = viewerRef.current?.viewerRenderer.renderer;
              if (renderer) {
                removeMeshes();
                setGpuMemoryCounter(WebGLMultiContextResourceTracker.instance?.getTotalMemoryUsage() ?? 0);
              }
            }}
          >
            Clear
          </button>
          <Select
            value={GeometryStressTestType[testType]}
            options={[
              {
                label: "Geometry reallocation",
                value: GeometryStressTestType[GeometryStressTestType.GEOMETRY_REALLOCATION],
              },
            ]}
            onChange={(val) => setTestType(GeometryStressTestType[val as string])}
            disabled={testRunning}
          />
          <Checkbox
            label="Automatically stop after 200 iterations"
            checked={autoStop}
            onChange={(e) => setAutoStop(e.target.checked)}
          />
          <div>Latest iteration time measurement: {lastPerformanceResult.toFixed(2)}</div>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/gfx-stress/geometry-stress-incremental")({
  component: withAuthentication(GeometryStressTestPage, { superAdmin: true }),
});
